import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Controller, FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { Input } from "../../atoms/Input";
import { Button } from "../../atoms/Button";
import FormTemplate from "../../templates/FormTemplate";
import { ImageUpload } from "../../atoms/ImageUpload";
import { MultiImageUpload } from "../../atoms/MultiImageUpload";
import { Select } from "../../atoms/Select";
import { Textarea } from "../../atoms/Textarea";
import { Checkbox } from "../../atoms/Checkbox";
import { MultiSelect } from "../../atoms/MultiSelect";
import { useDispatch, useSelector } from "react-redux";
import { propertyActions } from '../../../ducks/property/actions';
import { propertySelectors } from '../../../ducks/property/selectors';
import type { AppDispatch } from '../../../store';


export type PropertyFormData = {
    propertyCode: string;
    propertyName: string;
    propertyCategory: string;
    propertyType: string;
    propertyStatus: string;
    owner: string;
    city: string;
    district: string;
    commune: string;
    price: string;
    size: string;
    landArea: string;
    bedrooms: string;
    bathrooms: string;
    popularPlace: string;
    features: string[];
    linkYoutube: string;
    googleMapLink: string;
    description: string;
    images: File[];
    coverImage: File | string | null;
    isAvailable: boolean;
    isHot: boolean;
    deletedImageIndices?: number[];
    deleteCoverImage?: boolean;
};

type Props = {
    handleSubmit: (data: PropertyFormData, onError: (message: string) => void) => void | Promise<void>;
    defaultValues?: Partial<PropertyFormData>;
    mode?: "create" | "edit";
};

export const PropertyForm: React.FC<Props> = ({
    handleSubmit,
    defaultValues = {},
    mode = "create"
}) => {

    const [coverImage, setCoverImage] = useState<File | null>(
        defaultValues.coverImage && defaultValues.coverImage instanceof File ? defaultValues.coverImage : null
    );
    const [errorMessage, setErrorMessage] = useState<string>();
    const [methods, setMethods] = useState<UseFormReturn<FieldValues, any, undefined> | null>(null);
    const [deletedImageIndices, setDeletedImageIndices] = useState<number[]>([]);

    // Local submission state to prevent multiple clicks
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Track if cover image was explicitly removed by user
    const [isCoverImageRemoved, setIsCoverImageRemoved] = useState(false);

    // Helper function to trigger validation with logging
    const triggerValidation = useCallback((fieldName?: string | string[]) => {
        if (methods) {
            // console.log(`Triggering validation${fieldName ? ` for ${Array.isArray(fieldName) ? fieldName.join(', ') : fieldName}` : ''}`);
            if (fieldName) {
                methods.trigger(fieldName);
            } else {
                methods.trigger();
            }
            setTimeout(() => {
                // console.log('Form state after validation:', {
                //     isValid: methods.formState.isValid,
                //     errors: methods.formState.errors,
                //     values: methods.getValues()
                // });
            }, 50);
        }
    }, [methods]);

    const dispatch = useDispatch<AppDispatch>();
    const propertyCategories = useSelector(propertySelectors.propertyCategories);
    const propertyTypes = useSelector(propertySelectors.propertyTypes);
    const propertyOwners = useSelector(propertySelectors.propertyOwners);
    const cities = useSelector(propertySelectors.cities);
    const districts = useSelector(propertySelectors.districts);
    const communes = useSelector(propertySelectors.communes);
    const popularPlaces = useSelector(propertySelectors.popularPlaces);
    const features = useSelector(propertySelectors.features);

    useEffect(() => {
        dispatch(propertyActions.getPropertyCategory.started({}) as any);
        dispatch(propertyActions.getPropertyOwner.started({}) as any);
        dispatch(propertyActions.getCity.started({}) as any);
        dispatch(propertyActions.getFeatureList.started({}) as any);
        dispatch(propertyActions.getPopularPlaceList.started({}) as any);
    }, [dispatch]);

    // Auto-select Phnom Penh when cities are loaded in create mode
    useEffect(() => {
        if (mode === "create" && cities.length > 0 && methods) {
            const phnomPenh = cities.find(city => city.name === "Phnom Penh" || city.id === "1");
            if (phnomPenh && !methods.getValues("city")) {
                methods.setValue("city", phnomPenh.id);
                // Trigger district loading for Phnom Penh
                dispatch(propertyActions.getDistrict.started({ id: phnomPenh.id }));
                triggerValidation("city");
            }
        }
    }, [mode, cities, methods, dispatch, triggerValidation]);

    // Load cascading data for edit mode
    useEffect(() => {
        if (mode === "edit" && defaultValues.city && methods) {
            dispatch(propertyActions.getDistrict.started({ id: defaultValues.city }));
        }
    }, [mode, defaultValues.city, dispatch, methods]);

    useEffect(() => {
        if (mode === "edit" && defaultValues.district && methods) {
            dispatch(propertyActions.getCommune.started({ id: defaultValues.district }));
        }
    }, [mode, defaultValues.district, dispatch, methods]);

    // Load property types when property category is selected in edit mode
    useEffect(() => {
        if (mode === "edit" && defaultValues.propertyCategory && methods) {
            dispatch(propertyActions.getPropertyTypeByCategory.started({ category_id: defaultValues.propertyCategory }));
        }
    }, [mode, defaultValues.propertyCategory, dispatch, methods]);



    // Trigger initial validation when methods are ready
    useEffect(() => {
        if (methods) {
            setTimeout(() => {
                triggerValidation();
            }, 100);
        }
    }, [methods, triggerValidation]);

    // Additional validation trigger for edit mode after data loads
    useEffect(() => {
        if (mode === "edit" && methods && propertyCategories.length > 0 && propertyTypes.length > 0 && cities.length > 0) {
            setTimeout(() => {
                triggerValidation();
            }, 200);
        }
    }, [mode, methods, propertyCategories, propertyTypes, cities, districts, communes, triggerValidation]);

    // Handle cascading data loading in edit mode - only run once when options are loaded
    useEffect(() => {
        if (mode === "edit" && defaultValues) {
            // Load property types when property category is available
            if (defaultValues.propertyCategory && propertyCategories.length > 0) {
                dispatch(propertyActions.getPropertyTypeByCategory.started({ category_id: defaultValues.propertyCategory }));
            }
        }
    }, [mode, defaultValues?.propertyCategory, propertyCategories.length, dispatch]);

    useEffect(() => {
        if (mode === "edit" && defaultValues) {
            // Load districts when city is available
            if (defaultValues.city && cities.length > 0) {
                dispatch(propertyActions.getDistrict.started({ id: defaultValues.city }));
            }
        }
    }, [mode, defaultValues?.city, cities.length, dispatch]);

    useEffect(() => {
        if (mode === "edit" && defaultValues) {
            // Load communes when district is available
            if (defaultValues.district && districts.length > 0) {
                dispatch(propertyActions.getCommune.started({ id: defaultValues.district }));
            }
        }
    }, [mode, defaultValues?.district, districts.length, dispatch]);

    // Trigger validation when deletedImageIndices changes
    useEffect(() => {
        if (methods && mode === "edit") {
            setTimeout(() => {
                triggerValidation("images");
            }, 100);
        }
    }, [deletedImageIndices, methods, mode, triggerValidation]);

    // Ensure form validation is properly initialized in edit mode
    useEffect(() => {
        if (methods && mode === "edit" && defaultValues) {
            setTimeout(() => {
                triggerValidation();
            }, 300);
        }
    }, [methods, mode, defaultValues, triggerValidation]);

    // Debug form state changes
    useEffect(() => {
        if (methods && process.env.NODE_ENV === 'development') {
            console.log('Form state changed:', {
                isValid: methods.formState.isValid,
                errors: methods.formState.errors,
                isDirty: methods.formState.isDirty,
                values: methods.getValues()
            });
        }
    }, [methods?.formState.isValid, methods?.formState.errors, methods?.formState.isDirty]);

    // Auto-clean Google Map link if user pastes iframe code
    useEffect(() => {
        if (methods) {
            const subscription = methods.watch((value, { name, type }) => {
                if (name === 'googleMapLink' && value.googleMapLink && typeof value.googleMapLink === 'string') {
                    const link = value.googleMapLink;
                    if (link.includes('<iframe') && link.includes('src="')) {
                        const match = link.match(/src="([^"]+)"/);
                        if (match) {
                            // Extract URL and update field immediately
                            methods.setValue('googleMapLink', match[1], {
                                shouldValidate: true,
                                shouldDirty: true
                            });
                        }
                    }
                }
            });
            return () => subscription.unsubscribe();
        }
    }, [methods]);

    const handleCityChange = (value: string) => {
        if (methods) {
            methods.setValue("city", value);
            methods.setValue("district", "");
            methods.setValue("commune", "");
            triggerValidation(["city", "district", "commune"]);
        }
        if (value) {
            dispatch(propertyActions.getDistrict.started({ id: value }));
        }
    };

    // Handler for district change
    const handleDistrictChange = (value: string) => {
        if (methods) {
            methods.setValue("district", value);
            methods.setValue("commune", "");
            triggerValidation(["district", "commune"]);
        }
        if (value) {
            dispatch(propertyActions.getCommune.started({ id: value }));
        }
    };

    const handlePropertyCategoryChange = (value: string) => {
        if (methods) {
            methods.setValue("propertyCategory", value);
            methods.setValue("propertyType", "");
            triggerValidation(["propertyCategory", "propertyType"])
        }
        if (value) {
            dispatch(propertyActions.getPropertyTypeByCategory.started({ category_id: value }))
        }
    }

    // Helper to convert to SelectOptionType
    const toOptions = (arr?: { id: string; name: string }[]) => (arr ?? []).map(v => ({ id: v.id, name: v.name, value: v.id }));
    // Helper to convert to MultiSelectOptionType
    const toMultiOptions = (arr?: { id: string; name: string }[]) => (arr ?? []).map(v => ({ id: v.id, name: v.name, value: v.id }));

    // Zod schema for validation
    const schema = useMemo(() => {
        return z.object({
            propertyName: z.string().min(1, "Property Name is required"),
            propertyCategory: z.union([z.string(), z.number()]).transform(val => String(val)).pipe(z.string().min(1, "Property Category is required")),
            propertyType: z.union([z.string(), z.number()]).transform(val => String(val)).pipe(z.string().min(1, "Property Type is required")),
            propertyStatus: z.string().min(1, "Property Status is required"),
            owner: z.union([z.string(), z.number()]).transform(val => String(val)).optional(),
            city: z.union([z.string(), z.number()]).transform(val => String(val)).pipe(z.string().min(1, "City is required")),
            district: z.union([z.string(), z.number()]).transform(val => String(val)).pipe(z.string().min(1, "District is required")),
            commune: z.union([z.string(), z.number()]).transform(val => String(val)).pipe(z.string().min(1, "Commune is required")),
            price: z.string().min(1, "Price is required"),
            size: z.string().optional(),
            landArea: z.string().optional(),
            bedrooms: z.string().min(1, "Bedrooms is required"),
            bathrooms: z.string().min(1, "Bathrooms is required"),
            popularPlace: z.union([z.string(), z.number()]).transform(val => String(val)).optional(),
            features: z.array(z.union([z.string(), z.number()])).transform(val => val.map(v => String(v))).optional(),
            linkYoutube: z.string().optional(),
            googleMapLink: z.string().optional(),
            description: z.string().optional(),
            images: z.array(z.any()).refine((files) => {
                if (mode === "create") {
                    return files.length > 0;
                } else {
                    // In edit mode, we need at least one image (either new uploads or existing ones not deleted)
                    const newImages = files.filter(img => img instanceof File);
                    const existingImages = defaultValues?.images || [];
                    const deletedIndices = deletedImageIndices || [];
                    const remainingExistingImages = existingImages.length - deletedIndices.length;
                    const result = newImages.length + remainingExistingImages > 0;
                    // console.log('Image validation result:', { newImages: newImages.length, existingImages: existingImages.length, deletedIndices, remainingExistingImages, result });
                    return result;
                }
            }, "At least one property image is required"),
            coverImage: z.any().refine((file) => {
                if (mode === "create") {
                    return file instanceof File;
                } else {
                    const result = file instanceof File || (typeof file === 'string' && file.length > 0) ||
                        (defaultValues.coverImage && typeof defaultValues.coverImage === 'string');
                    return result;
                }
            }, "Cover image is required"),
            isAvailable: z.boolean().optional(),
            isHot: z.boolean().optional(),
            propertyCode: z.string().optional(),
        });
    }, [mode, defaultValues, deletedImageIndices]);

    return (
        <FormTemplate
            schema={schema}
            setMethods={setMethods}
            isSubmitting={isSubmitting}
            defaultValues={(() => {
                if (mode === "edit") {
                    const processedDefaults = {
                        propertyCode: defaultValues.propertyCode || "Auto-generated after create",
                        propertyName: defaultValues.propertyName || "",
                        propertyCategory: defaultValues.propertyCategory || "",
                        propertyType: defaultValues.propertyType || "",
                        propertyStatus: defaultValues.propertyStatus || "",
                        owner: defaultValues.owner || "",
                        city: defaultValues.city || "",
                        district: defaultValues.district || "",
                        commune: defaultValues.commune || "",
                        price: defaultValues.price ? String(parseInt(String(defaultValues.price), 10)) : "",
                        size: defaultValues.size ? String(defaultValues.size) : "",
                        landArea: defaultValues.landArea || "",
                        bedrooms: defaultValues.bedrooms ? String(defaultValues.bedrooms) : "",
                        bathrooms: defaultValues.bathrooms ? String(defaultValues.bathrooms) : "",
                        popularPlace: defaultValues.popularPlace || "",
                        features: defaultValues.features || [],
                        linkYoutube: defaultValues.linkYoutube || "",
                        googleMapLink: defaultValues.googleMapLink || "",
                        description: defaultValues.description || "",
                        images: [], // In edit mode, start with empty array for new uploads only
                        coverImage: defaultValues.coverImage || null,
                        isAvailable: typeof defaultValues.isAvailable === 'boolean' ? defaultValues.isAvailable : true,
                        isHot: typeof defaultValues.isHot === 'boolean' ? defaultValues.isHot : false,
                    };


                    return processedDefaults;
                } else {
                    // Find Phnom Penh in cities array for default value
                    const phnomPenh = cities.find(city => city.name === "Phnom Penh" || city.id === "1");
                    return {
                        propertyCode: "Auto-generated after create",
                        propertyName: "",
                        propertyCategory: "",
                        propertyType: "",
                        propertyStatus: "",
                        owner: "",
                        city: phnomPenh?.id || "",
                        district: "",
                        commune: "",
                        price: "",
                        size: "",
                        landArea: "",
                        bedrooms: "",
                        bathrooms: "",
                        popularPlace: "",
                        features: [],
                        linkYoutube: "",
                        googleMapLink: "",
                        description: "",
                        images: [],
                        coverImage: null,
                        isAvailable: true,
                        isHot: false,
                    };
                }
            })()}
            key={`${mode}-${defaultValues?.propertyName || 'new'}-${cities.length}`}
            mode="onChange"
            onSubmit={async (data) => {
                setIsSubmitting(true);

                try {
                    const typedData = data as PropertyFormData;
                    setErrorMessage(undefined);
                    const featuresArray = Array.isArray(typedData.features) ? typedData.features.map((f: any) => typeof f === 'object' ? f.id || f.value : f) : [];

                    // Handle existing images - filter out deleted ones
                    let finalImages = typedData.images || [];
                    // Only include File objects (new uploads), not existing image URLs
                    finalImages = finalImages.filter(img => img instanceof File);

                    const fullData = {
                        ...typedData,
                        features: featuresArray,
                        images: finalImages,
                        coverImage: coverImage || (defaultValues.coverImage && typeof defaultValues.coverImage === 'string' && !isCoverImageRemoved ? defaultValues.coverImage : null),
                        deletedImageIndices: mode === "edit" ? deletedImageIndices : undefined,
                        deleteCoverImage: mode === "edit" && isCoverImageRemoved ? true : undefined,
                    };

                    // Call handleSubmit and await if it returns a Promise
                    const result = handleSubmit(fullData as PropertyFormData, (message) => {
                        setErrorMessage(message);
                        setIsSubmitting(false);
                    });

                    // If handleSubmit returns a Promise, await it
                    if (result && typeof result.then === 'function') {
                        await result;
                    }
                } catch (error: any) {
                    // Error is already handled by setting errorMessage in the callback passed to handleSubmit
                    // We just need to ensure isSubmitting is reset so the button can be clicked again
                    console.log('Submission error caught in PropertyForm:', error.message);
                    setIsSubmitting(false);
                }
            }}
        >
            <StyledHeaderRow>
                <Controller
                    name="isAvailable"
                    control={methods?.control}
                    render={({ field }) => (
                        <Checkbox
                            name="isAvailable"
                            label="Is Available?"
                            checked={Boolean(field.value)}
                            variant="empty"
                            onChange={(checked) => {
                                field.onChange(checked);
                                triggerValidation('isAvailable');
                            }}
                        />
                    )}
                />
                <Controller
                    name="isHot"
                    control={methods?.control}
                    render={({ field }) => (
                        <Checkbox
                            name="isHot"
                            label="Is Hot Property?"
                            checked={Boolean(field.value)}
                            variant="empty"
                            onChange={(checked) => {
                                field.onChange(checked);
                                triggerValidation('isHot');
                            }}
                        />
                    )}
                />
            </StyledHeaderRow>
            <StyledFormGrid>
                {/* Property Code */}
                <Input
                    label="Property Code"
                    name="propertyCode"
                    type="text"
                    value={methods?.getValues("propertyCode")}
                    disabled
                    fullWidth
                />

                {/* Property Name */}
                <Controller
                    name="propertyName"
                    control={methods?.control}
                    render={({ field }) => (
                        <Input
                            name="propertyName"
                            label="Property Name *"
                            placeholder="Enter property name"
                            required
                            fullWidth
                            value={field.value || ""}
                            onChange={(value) => {
                                field.onChange(value);
                                triggerValidation("propertyName");
                            }}
                        />
                    )}
                />

                {/* Property Category */}
                <Controller
                    name="propertyCategory"
                    control={methods?.control}
                    render={({ field }) => (
                        <Select
                            name="propertyCategory"
                            label="Property Category *"
                            options={toOptions(propertyCategories)}
                            onChange={(_, value) => {
                                field.onChange(value);
                                handlePropertyCategoryChange(value);
                                triggerValidation("propertyCategory");
                            }}
                            defaultValue={field.value}
                            required
                            hasSearchInput={false}
                        />
                    )}
                />

                {/* Property Type */}
                <Controller
                    name="propertyType"
                    control={methods?.control}
                    render={({ field }) => (
                        <Select
                            name="propertyType"
                            label="Property Type *"
                            options={toOptions(propertyTypes)}
                            onChange={(_, value) => {
                                field.onChange(value);
                                triggerValidation("propertyType");
                            }}
                            defaultValue={field.value}
                            required
                            disabled={!methods?.getValues("propertyCategory")}
                            hasSearchInput={false}
                        />
                    )}
                />

                {/* Property Status */}
                <Controller
                    name="propertyStatus"
                    control={methods?.control}
                    render={({ field }) => (
                        <Select
                            name="propertyStatus"
                            label="Property Status *"
                            options={[
                                { id: 'rent', name: 'Rent', value: 'rent' },
                                { id: 'sale', name: 'Sale', value: 'sale' },
                            ]}
                            onChange={(_, value) => {
                                field.onChange(value);
                                triggerValidation("propertyStatus");
                            }}
                            defaultValue={field.value}
                            required
                            hasSearchInput={false}
                        />
                    )}
                />

                {/* Owner */}
                <Controller
                    name="owner"
                    control={methods?.control}
                    render={({ field }) => (
                        <Select
                            name="owner"
                            label="Owner"
                            options={toOptions(propertyOwners)}
                            onChange={(_, value) => {
                                field.onChange(value);
                                triggerValidation("owner");
                            }}
                            defaultValue={field.value}
                        />
                    )}
                />

                {/* City */}
                <Controller
                    name="city"
                    control={methods?.control}
                    render={({ field }) => (
                        <Select
                            name="city"
                            label="City"
                            options={toOptions(cities)}
                            onChange={(_, value) => {
                                field.onChange(value);
                                handleCityChange(value);
                            }}
                            defaultValue={field.value}
                            required
                            hasSearchInput={false}
                        />
                    )}
                />

                {/* District */}
                <Controller
                    name="district"
                    control={methods?.control}
                    render={({ field }) => (
                        <Select
                            name="district"
                            label="District"
                            options={toOptions(districts)}
                            onChange={(_, value) => {
                                field.onChange(value);
                                handleDistrictChange(value);
                            }}
                            defaultValue={field.value}
                            required
                            disabled={!methods?.getValues("city")}
                        />
                    )}
                />

                {/* Commune */}
                <Controller
                    name="commune"
                    control={methods?.control}
                    render={({ field }) => (
                        <Select
                            name="commune"
                            label="Commune"
                            options={toOptions(communes)}
                            onChange={(_, value) => {
                                field.onChange(value);
                                triggerValidation("commune");
                            }}
                            defaultValue={field.value}
                            required
                            disabled={!methods?.getValues("district")}
                        />
                    )}
                />

                {/* Price */}
                <Controller
                    name="price"
                    control={methods?.control}
                    render={({ field }) => (
                        <Input
                            name="price"
                            label="Price *"
                            type="number"
                            placeholder="Enter price"
                            required
                            fullWidth
                            value={field.value || ""}
                            onChange={(value) => {
                                field.onChange(value);
                                triggerValidation("price");
                            }}
                        />
                    )}
                />

                {/* Size */}
                <Controller
                    name="size"
                    control={methods?.control}
                    render={({ field }) => (
                        <Input
                            name="size"
                            label="Size"
                            type="text"
                            placeholder="Enter size"
                            fullWidth
                            value={field.value || ""}
                            onChange={(value) => {
                                field.onChange(value);
                                triggerValidation("size");
                            }}
                        />
                    )}
                />

                {/* Land Area */}
                <Controller
                    name="landArea"
                    control={methods?.control}
                    render={({ field }) => (
                        <Input
                            name="landArea"
                            label="Land Area"
                            type="text"
                            placeholder="12 x 20"
                            fullWidth
                            value={field.value || ""}
                            onChange={(value) => {
                                field.onChange(value);
                                triggerValidation("landArea");
                            }}
                        />
                    )}
                />

                {/* Bedrooms */}
                <Controller
                    name="bedrooms"
                    control={methods?.control}
                    render={({ field }) => (
                        <Input
                            name="bedrooms"
                            label="Bedrooms *"
                            type="number"
                            placeholder="Enter bedrooms"
                            required
                            fullWidth
                            value={field.value || ""}
                            onChange={(value) => {
                                field.onChange(value);
                                triggerValidation("bedrooms");
                            }}
                        />
                    )}
                />

                {/* Bathrooms */}
                <Controller
                    name="bathrooms"
                    control={methods?.control}
                    render={({ field }) => (
                        <Input
                            name="bathrooms"
                            label="Bathrooms *"
                            type="number"
                            placeholder="Enter bathrooms"
                            required
                            fullWidth
                            value={field.value || ""}
                            onChange={(value) => {
                                field.onChange(value);
                                triggerValidation("bathrooms");
                            }}
                        />
                    )}
                />

                {/* Popular Place */}
                <Controller
                    name="popularPlace"
                    control={methods?.control}
                    render={({ field }) => (
                        <Select
                            name="popularPlace"
                            label="Popular Place"
                            options={[{ id: "", name: "None", value: "" }, ...toOptions(popularPlaces)]}

                            onChange={(_, value) => {
                                field.onChange(value);
                                triggerValidation("popularPlace");
                            }}
                            defaultValue={field.value}
                        />
                    )}
                />

                {/* Features */}
                <Controller
                    name="features"
                    control={methods?.control}
                    render={({ field }) => {
                        const selectedValues = Array.isArray(field.value) ? field.value : [];
                        return (
                            <StyledGridSpan2>
                                <MultiSelect
                                    name="features"
                                    label="Features"
                                    options={toMultiOptions(features)}
                                    selectedValues={selectedValues}
                                    onChange={(values) => {
                                        field.onChange(values);
                                        triggerValidation("features");
                                    }}
                                />
                            </StyledGridSpan2>
                        );
                    }}
                />

                {/* Link Youtube */}
                <Controller
                    name="linkYoutube"
                    control={methods?.control}
                    render={({ field }) => (
                        <Input
                            name="linkYoutube"
                            label="Link Youtube"
                            type="text"
                            placeholder="Youtube link"
                            fullWidth
                            value={field.value || ""}
                            onChange={(value) => {
                                field.onChange(value);
                                triggerValidation("linkYoutube");
                            }}
                        />
                    )}
                />

                {/* Google map link */}
                <Controller
                    name="googleMapLink"
                    control={methods?.control}
                    render={({ field }) => (
                        <StyledGridSpan3>
                            <Input
                                name="googleMapLink"
                                label="Google map link"
                                type="text"
                                placeholder="Google map link"
                                fullWidth
                                value={field.value || ""}
                                onChange={(value) => {
                                    field.onChange(value);
                                    triggerValidation("googleMapLink");
                                }}
                            />
                        </StyledGridSpan3>
                    )}
                />

                {/* Description */}
                <StyledGridSpan3>
                    <Controller
                        name="description"
                        control={methods?.control}
                        render={({ field }) => (
                            <Textarea
                                label="Description"
                                name="description"
                                placeholder="Enter description"
                                rows={5}
                                fullWidth
                                value={field.value || ""}
                                onChange={(value) => {
                                    field.onChange(value);
                                    triggerValidation("description");
                                }}
                            />
                        )}
                    />
                </StyledGridSpan3>
            </StyledFormGrid>

            {/* Image Uploads */}
            <StyledUploadSection>
                <Controller
                    name="images"
                    control={methods?.control}
                    render={({ field }) => {
                        // Pass original images to MultiImageUpload, let it handle deletion display
                        const originalImages = mode === "edit" && defaultValues.images ? defaultValues.images : [];

                        // console.log('PropertyForm: Rendering MultiImageUpload with:', {
                        //     originalImages: originalImages.length,
                        //     deletedImageIndices,
                        //     defaultValuesImages: defaultValues.images?.length
                        // });

                        return (
                            <MultiImageUpload
                                name="images"
                                label="Property Images "
                                required={true}
                                value={field.value || []}
                                onChange={(files) => {
                                    field.onChange(files);
                                    triggerValidation("images");
                                }}
                                maxFiles={30}
                                fullWidth
                                existingImages={originalImages}
                                deletedIndices={deletedImageIndices}
                                onExistingImagesChange={(deletedIndices) => {
                                    // console.log('PropertyForm: onExistingImagesChange called with:', deletedIndices);
                                    // console.log('PropertyForm: Current deletedImageIndices state:', deletedImageIndices);
                                    setDeletedImageIndices(deletedIndices);
                                }}
                            />
                        );
                    }}
                />
            </StyledUploadSection>

            <StyledUploadSection>
                <Controller
                    name="coverImage"
                    control={methods?.control}
                    render={({ field }) => {
                        return (
                            <ImageUpload
                                name="coverImage"
                                label="Cover Image"
                                required={true}
                                value={coverImage}
                                onChange={(file) => {
                                    setCoverImage(file);
                                    field.onChange(file);
                                    if (file) {
                                        setIsCoverImageRemoved(false); // Reset removal flag when new image is selected
                                    }
                                    triggerValidation("coverImage");
                                }}
                                onImageRemove={() => {
                                    setIsCoverImageRemoved(true); // Mark cover image as explicitly removed
                                    setCoverImage(null);
                                    field.onChange(null);
                                    triggerValidation("coverImage");
                                }}
                                existingImageUrl={mode === "edit" && defaultValues.coverImage && typeof defaultValues.coverImage === 'string' && !isCoverImageRemoved ? defaultValues.coverImage : undefined}
                                fullWidth
                            />
                        );
                    }}
                />
            </StyledUploadSection>

            <StyledFooter>
                <Button
                    variant="filled"
                    text={isSubmitting ? (mode === "create" ? "Creating..." : "Updating...") : (mode === "create" ? "Create" : "Update")}
                    type="submit"
                    disabled={isSubmitting}
                    isSubmitting={isSubmitting}
                />
            </StyledFooter>
            {errorMessage && <StyledError>{errorMessage}</StyledError>}
        </FormTemplate>
    );
};

const StyledFormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px 16px;
  margin-bottom: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px 12px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    margin-bottom: 16px;
  }
`;

const StyledUploadSection = styled.div`
  display: flex;
  gap: 32px;
  margin: 24px 0 0 0;
  align-items: flex-start;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 20px;
    margin: 20px 0 0 0;
  }
  
  @media (max-width: 480px) {
    gap: 16px;
    margin: 16px 0 0 0;
  }
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
  
  @media (max-width: 768px) {
    margin-top: 24px;
  }
  
  @media (max-width: 480px) {
    margin-top: 20px;
  }
`;

const StyledError = styled.div`
  color: #d32f2f;
  margin-top: 12px;
  text-align: right;
  
  @media (max-width: 480px) {
    text-align: center;
    margin-top: 8px;
  }
`;

const StyledGridSpan2 = styled.div`
  grid-column: span 2;
  
  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const StyledGridSpan3 = styled.div`
  grid-column: span 3;
  
  @media (max-width: 1024px) {
    grid-column: span 2;
  }
  
  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const StyledHeaderRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 24px;
  width: 100%;
  margin-bottom: 12px;
  background: none;
  box-shadow: none;
  
  @media (max-width: 768px) {
    margin-bottom: 8px;
  }
`;